/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { format, addDays } from 'date-fns';
import axiosClient from '../../api/axiosClient';
import PortalModal from '../../components/shared/PortalModal';
import { buildShopScopedPath, readStoredAuthUser, resolveUserShopId } from '../../utils/adminShopContext';

// Brand accent color
const ACCENT = '#7d6633';

// Icons using SVG for consistency with client page style
const Icons = {
  Loading: () => (
    <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Plus: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Search: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Calendar: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Clock: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  User: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )
};

const CreateBookingModal = ({ open, onClose, onSubmit, userRole, userId }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    client_id: '',
    barber_id: '',
    service_id: '',
    start_utc: ''
  });
  const [clients, setClients] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState('');
  // Default to UTC; overwritten by API-provided salon timezone.
  const [shopTimezone, setShopTimezone] = useState(
    'UTC'
  );
  const [salonSettings, setSalonSettings] = useState(null);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(30); // Default fallback
  const [calendarData, setCalendarData] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      console.log('Loading clients...');
      const response = await axiosClient.get('/admin/clients?status=active&pageSize=100');
      console.log('Clients response:', response.data);
      const clientsData = response.data.data || [];
      console.log('Clients loaded:', clientsData.length, 'clients:', clientsData.map(c => c.name));
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  }, []);

  const loadBarbers = useCallback(async () => {
    try {
      console.log('Loading barbers...');
      const response = await axiosClient.get('/admin/barbers?status=active&pageSize=100');
      console.log('Barbers response:', response.data);
      setBarbers(response.data.data || []);
      console.log('Barbers loaded:', response.data.data?.length || 0, 'barbers');
      
      // If user is barber, preselect themselves
      if (userRole === 'barber' && userId) {
        setFormData(prev => ({ ...prev, barber_id: userId.toString() }));
      }
    } catch (error) {
      console.error('Failed to load barbers:', error);
    }
  }, [userRole, userId]);

  const loadServices = useCallback(async () => {
    try {
      console.log('Loading services...');
      // Try the correct endpoints based on backend routes
      let response;
      let servicesData = [];
      
      try {
        // Try the main services endpoint first
        console.log('Trying /services endpoint...');
        response = await axiosClient.get('/services');
        console.log('Services response from /services:', response.data);
        
        if (Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (response.data.data) {
          servicesData = response.data.data;
        }
        
      } catch (firstError) {
        console.log('First endpoint failed:', firstError.message);
        try {
          // Try admin services endpoint
          console.log('Trying /admin/services endpoint...');
          response = await axiosClient.get('/admin/services');
          console.log('Services response from /admin/services:', response.data);
          
          if (Array.isArray(response.data)) {
            servicesData = response.data;
          } else if (response.data.data) {
            servicesData = response.data.data;
          }
          
        } catch (secondError) {
          console.log('Second endpoint failed:', secondError.message);
          throw secondError;
        }
      }
      
      console.log('Services loaded:', servicesData.length, 'services:', servicesData);
      
      if (servicesData.length === 0) {
        console.log('No services found, using mock services for testing');
        // Set mock services for testing
        servicesData = [
          { id: 'mock-1', name: 'Classic Haircut', description: 'Professional haircut and styling', duration_min: 30, price_cents: 2500 },
          { id: 'mock-2', name: 'Beard Trim', description: 'Beard trimming and shaping', duration_min: 15, price_cents: 1500 },
          { id: 'mock-3', name: 'Hair Wash & Style', description: 'Shampoo, condition and style', duration_min: 20, price_cents: 1000 },
          { id: 'mock-4', name: 'Skin Fade', description: 'Modern skin fade cut', duration_min: 45, price_cents: 4000 }
        ];
      }
      
      setServices(servicesData);
      
    } catch (error) {
      console.error('All service endpoints failed:', error);
      
      // Always provide mock services as fallback
      const mockServices = [
        { id: 'mock-1', name: 'Classic Haircut', description: 'Professional haircut and styling', duration_min: 30, price_cents: 2500 },
        { id: 'mock-2', name: 'Beard Trim', description: 'Beard trimming and shaping', duration_min: 15, price_cents: 1500 },
        { id: 'mock-3', name: 'Hair Wash & Style', description: 'Shampoo, condition and style', duration_min: 20, price_cents: 1000 },
        { id: 'mock-4', name: 'Skin Fade', description: 'Modern skin fade cut', duration_min: 45, price_cents: 4000 }
      ];
      
      console.log('Using mock services as fallback:', mockServices);
      setServices(mockServices);
    }
  }, []);

  const loadSalonSettings = useCallback(async () => {
    try {
      console.log('Loading salon settings...');
      const storedUser = readStoredAuthUser();
      const shopId = resolveUserShopId(storedUser);
      const response = await axiosClient.get(buildShopScopedPath('/admin/settings/shop', shopId));
      console.log('Salon settings response:', response.data);
      
      if (response.data.bookingAdvanceDays) {
        const settings = response.data;
        setSalonSettings(settings);
        setMaxAdvanceDays(settings.bookingAdvanceDays || 30);
        console.log('✅ Salon settings loaded - Max advance days:', settings.bookingAdvanceDays);
      } else {
        console.log('⚠️ Salon settings loaded but no bookingAdvanceDays found, using default 120');
        setMaxAdvanceDays(120); // Default to 120 days as mentioned by user
      }
    } catch (error) {
      console.error('Failed to load salon settings:', error);
      console.log('⚠️ Using default 120 days advance booking');
      setMaxAdvanceDays(120); // Default to 120 days as mentioned by user
    }
  }, []);

  const loadCalendarData = useCallback(async () => {
    if (!formData.barber_id || !formData.service_id) return;
    
    try {
      setLoadingCalendar(true);
      console.log('📅 Loading calendar data for:', {
        barber_id: formData.barber_id,
        service_id: formData.service_id,
        days: maxAdvanceDays
      });
      
      const params = new URLSearchParams({
        barber_id: formData.barber_id,
        service_id: formData.service_id,
        start_date: format(new Date(), 'yyyy-MM-dd'),
        days: maxAdvanceDays.toString()
      });

      const response = await axiosClient.get(`/portal/slots?${params}`);
      console.log('📅 Calendar data response:', response.data);
      
      if (response.data.ok && response.data.data?.slots) {
        setCalendarData(response.data.data.slots);
      }
    } catch (error) {
      console.error('❌ Failed to load calendar data:', error);
      setCalendarData([]);
    } finally {
      setLoadingCalendar(false);
    }
  }, [formData.barber_id, formData.service_id, maxAdvanceDays]);

  const loadSlots = useCallback(async () => {
    try {
      console.log('🕐 Loading slots for:', {
        barber_id: formData.barber_id,
        service_id: formData.service_id,
        selectedDate: selectedDate
      });
      setLoadingSlots(true);
      const params = new URLSearchParams({
        barber_id: formData.barber_id,
        service_id: formData.service_id,
        start_date: selectedDate,
        days: '1'
      });

      console.log('📡 Making slots API call:', `/portal/slots?${params}`);
      const response = await axiosClient.get(`/portal/slots?${params}`);
      console.log('📡 Slots API response:', response.data);
      
      // Extract slots from response and set timezone
      if (response.data.ok && response.data.data?.slots && response.data.data.slots.length > 0) {
        setSlots(response.data.data.slots[0]?.slots || []);
        console.log('✅ Slots loaded:', response.data.data.slots[0]?.slots?.length || 0, 'slots');
      } else {
        setSlots([]);
        console.log('❌ No slots available');
      }
      
      if (response.data.data?.timezone) {
        setShopTimezone(response.data.data.timezone);
        try { localStorage.setItem('shopTimezone', response.data.data.timezone); } catch {}
      }
    } catch (error) {
      console.error('❌ Failed to load slots:', error);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [formData.barber_id, formData.service_id, selectedDate]);

  // Load data on mount
  useEffect(() => {
    if (open) {
      loadClients();
      loadBarbers();
      loadServices();
      loadSalonSettings();
      setClientQuery('');
      setClientDropdownOpen(false);
    }
  }, [open, loadClients, loadBarbers, loadServices, loadSalonSettings]);

  // Load calendar data when barber/service changes
  useEffect(() => {
    if (formData.barber_id && formData.service_id && maxAdvanceDays > 0) {
      loadCalendarData();
    } else {
      setCalendarData([]);
    }
  }, [formData.barber_id, formData.service_id, maxAdvanceDays, loadCalendarData]);

  // Load slots when barber, service, or date changes
  useEffect(() => {
    if (formData.barber_id && formData.service_id && selectedDate) {
      loadSlots();
    } else {
      setSlots([]);
    }
  }, [formData.barber_id, formData.service_id, selectedDate, loadSlots]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setFormData(prev => ({ ...prev, start_utc: '' }));
    setError('');
  };

  const handleCalendarDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    setFormData(prev => ({ ...prev, start_utc: '' }));
    setError('');
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const handleSlotSelect = (timeSlot, event) => {
    console.log('🕐 Time slot clicked:', timeSlot);
    console.log('📅 Selected date:', selectedDate);
    console.log('🌍 Shop timezone:', shopTimezone);
    
    // Explicitly prevent any form submission
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (timeSlot && selectedDate) {
      let utcDateTime = '';
      
      // CRITICAL: ALWAYS prefer the UTC field from slot object
      if (typeof timeSlot === 'object' && timeSlot !== null) {
        utcDateTime =
          (typeof timeSlot.utc === 'string' && timeSlot.utc)
          || (typeof timeSlot.start_utc === 'string' && timeSlot.start_utc)
          || (typeof timeSlot.startUtc === 'string' && timeSlot.startUtc)
          || '';
        
        if (utcDateTime) {
          console.log('✅ Using UTC from slot object:', utcDateTime);
        }
      }

      // FALLBACK: Only if slot doesn't have UTC field (should rarely happen)
      if (!utcDateTime) {
        console.warn('⚠️ Slot missing UTC field, attempting manual conversion...');
        const slotValue = typeof timeSlot === 'string' ? timeSlot : (timeSlot.time || timeSlot.label || '');

        if (!slotValue) {
          console.error('❌ No time value found in slot:', timeSlot);
          return false;
        }

        // Check if already a full ISO string with Z (UTC)
        if (slotValue.includes('T') && slotValue.includes('Z')) {
          utcDateTime = slotValue;
          console.log('✅ Using UTC ISO string:', utcDateTime);
      } else {
          // PROBLEM: Browser timezone conversion is unreliable!
          // We need the shop's timezone but don't have moment-timezone in frontend
          // BEST SOLUTION: Backend should ALWAYS provide UTC in slot.utc field
          console.error('❌ Cannot reliably convert to UTC without shop timezone library!');
          console.error('📍 Slot missing UTC field. Backend must provide slot.utc');
          console.error('🔧 Attempted value:', slotValue);
          return false;
        }
      }

      if (utcDateTime) {
        console.log('📅 Final UTC time:', utcDateTime);
      setFormData(prev => ({ ...prev, start_utc: utcDateTime }));
      } else {
        console.error('❌ Failed to determine UTC time for slot');
      }
    } else {
      console.warn('⚠️ Missing timeSlot or selectedDate:', { timeSlot, selectedDate });
    }
    
    // Return false to prevent any further event handling
    return false;
  };

  const handleSubmit = async (e) => {
    console.log('🚀 handleSubmit called - this should only happen when clicking Create Booking button');
    
    // Prevent default form submission behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Additional guard: if this is called without proper form data, just return
    console.log('📋 Current form data:', formData);
    if (!formData.client_id || !formData.barber_id || !formData.service_id || !formData.start_utc) {
      console.log('⚠️ handleSubmit called but form data incomplete, ignoring...');
      console.log('Missing fields:', {
        client_id: !formData.client_id,
        barber_id: !formData.barber_id,
        service_id: !formData.service_id,
        start_utc: !formData.start_utc
      });
      setError('Please fill in all required fields');
      return;
    }
    
    console.log('✅ Form data is complete, proceeding with booking creation...');

    try {
      setLoading(true);
      await onSubmit(formData);
      handleClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      client_id: '',
      barber_id: '',
      service_id: '',
      start_utc: ''
    });
    setSelectedDate('');
    setSlots([]);
    setError('');
    setClientQuery('');
    setClientDropdownOpen(false);
    onClose();
  };

  const getSelectedClient = () => {
    return clients.find(c => c.id === parseInt(formData.client_id));
  };

  const getClientLabel = (client) => (
    `${client?.name || 'Unknown'}${client?.email ? ` (${client.email})` : ''}`
  );

  const normalizedClientSearch = clientQuery.trim().toLowerCase();
  const filteredClients = normalizedClientSearch
    ? clients.filter(client => {
      const name = client?.name?.toLowerCase() || '';
      const email = client?.email?.toLowerCase() || '';
      const phone = String(client?.phone || client?.phone_number || client?.phoneNumber || '').toLowerCase();
      return name.includes(normalizedClientSearch)
        || email.includes(normalizedClientSearch)
        || phone.includes(normalizedClientSearch);
    })
    : clients;

  if (!open) return null;

  return (
    <PortalModal open={open}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="hc-modal-panel bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-2xl p-4 md:p-6 w-full max-w-lg my-8 shadow-2xl"
        >
        <div className="flex items-center mb-4">
          <div className="h-8 w-8 rounded-lg bg-[--accent]/10 flex items-center justify-center mr-2" style={{ '--accent': ACCENT }}>
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Create New Booking
            </h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              Schedule a new appointment for a client
            </p>
          </div>
        </div>

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl mb-4 backdrop-blur-sm"
          >
            {error}
          </motion.div>
        )}

        <div>
          {/* Client Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Client *
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">
                <Icons.Search />
              </div>
              <input
                type="text"
                value={clientQuery}
                onChange={(e) => {
                  setClientQuery(e.target.value);
                  setClientDropdownOpen(true);
                  if (formData.client_id) {
                    handleInputChange('client_id', '');
                  }
                }}
                onFocus={() => setClientDropdownOpen(true)}
                onBlur={() => {
                  setTimeout(() => setClientDropdownOpen(false), 120);
                }}
                placeholder="Select or search a client..."
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              />
              {clientDropdownOpen && (
                <div className="absolute z-20 mt-2 w-full max-h-64 overflow-auto rounded-xl border border-white/20 dark:border-white/10 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl shadow-xl">
                  {filteredClients.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-zinc-500 dark:text-zinc-400">
                      No clients match your search
                    </div>
                  ) : filteredClients.map(client => (
                    <button
                      key={client.id}
                      type="button"
                      className="w-full text-left px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-colors"
                      onMouseDown={(e) => {
                        e.preventDefault();
                      }}
                      onClick={() => {
                        handleInputChange('client_id', client.user_id);
                        setClientQuery(getClientLabel(client));
                        setClientDropdownOpen(false);
                      }}
                    >
                      <div className="font-medium">{client.name}</div>
                      {client.email && (
                        <div className="text-xs text-zinc-500 dark:text-zinc-400">
                          {client.email}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Barber Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Barber *
            </label>
            <select
              value={formData.barber_id}
              onChange={(e) => handleInputChange('barber_id', e.target.value)}
              required
              disabled={userRole === 'barber'}
              className={`w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all ${userRole === 'barber' ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ '--accent': ACCENT }}
            >
              <option value="">Select a barber</option>
              {barbers.map(barber => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          </div>

          {/* Service Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Service *
            </label>
            <select
              value={formData.service_id}
              onChange={(e) => handleInputChange('service_id', e.target.value)}
              required
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
            >
              <option value="">Select a service</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name} - ${(service.price_cents / 100).toFixed(2)} ({service.duration_min} min)
                </option>
              ))}
            </select>
          </div>

          {/* Calendar Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Select Date *
            </label>
            
            {!formData.barber_id || !formData.service_id ? (
              <div className="p-4 rounded-2xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
                <div className="text-center">
                  <div className="h-8 w-8 mx-auto mb-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                    <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    Please select a barber and service first to see available dates
                  </p>
                </div>
              </div>
            ) : loadingCalendar ? (
              <div className="p-4 rounded-2xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
                <div className="flex items-center justify-center">
                  <Icons.Loading />
                  <span className="ml-2 text-sm text-zinc-500 dark:text-zinc-400">
                    Loading available dates...
                  </span>
                </div>
              </div>
            ) : (
              <div className="border border-white/20 dark:border-white/10 rounded-2xl p-4 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigateMonth(-1)}
                    className="p-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </motion.button>
                  
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h3>
                  
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => navigateMonth(1)}
                    className="p-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </motion.button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-2 mb-3">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                    <div key={day} className="text-center text-sm font-medium py-3 text-zinc-500 dark:text-zinc-400">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {(() => {
                    const year = currentMonth.getFullYear();
                    const month = currentMonth.getMonth();
                    const firstDay = new Date(year, month, 1);
                    const lastDay = new Date(year, month + 1, 0);
                    const startDate = new Date(firstDay);
                    startDate.setDate(startDate.getDate() - firstDay.getDay());
                    
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    const days = [];
                    for (let i = 0; i < 42; i++) {
                      const date = new Date(startDate);
                      date.setDate(startDate.getDate() + i);
                      
                      const dateStr = date.toISOString().split('T')[0];
                      const dayData = calendarData.find(d => d.date === dateStr);
                      const hasSlots = dayData?.slots?.length > 0;
                      const isPast = date < today;
                      const isCurrentMonth = date.getMonth() === month;
                      const isSelected = selectedDate === dateStr;
                      const isTooFar = date > addDays(today, maxAdvanceDays);
                      
                      days.push({ date, dateStr, hasSlots, isPast, isCurrentMonth, isSelected, isTooFar });
                    }
                    
                    return days.map((day, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: day.isPast || day.isTooFar || !day.hasSlots || day.isSelected ? 1 : 1.05 }}
                        whileTap={{ scale: day.isPast || day.isTooFar || !day.hasSlots ? 1 : 0.95 }}
                        onClick={() => !day.isPast && !day.isTooFar && day.hasSlots && handleCalendarDateSelect(day.dateStr)}
                        disabled={day.isPast || day.isTooFar || !day.hasSlots}
                        className={`relative p-3 text-sm rounded-xl transition-all duration-200 ${
                          day.isSelected
                            ? 'text-white shadow-lg'
                            : day.isPast || day.isTooFar
                            ? 'text-zinc-400 dark:text-zinc-600 cursor-not-allowed'
                            : day.hasSlots
                            ? 'text-zinc-900 dark:text-zinc-100 border border-white/20 dark:border-white/10 hover:bg-white/70 dark:hover:bg-zinc-800/70'
                            : 'text-zinc-400 dark:text-zinc-600 cursor-not-allowed'
                        } ${!day.isCurrentMonth ? 'opacity-50' : ''}`}
                        style={{
                          background: day.isSelected 
                            ? ACCENT 
                            : 'transparent'
                        }}
                      >
                        {day.date.getDate()}
                        
                        {/* Availability indicator */}
                        {day.hasSlots && !day.isPast && !day.isTooFar && (
                          <div className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
                            day.isSelected ? 'bg-white' : 'bg-green-500'
                          }`} />
                        )}
                      </motion.button>
                    ));
                  })()}
                </div>

                {/* Legend */}
                <div className="flex items-center justify-center space-x-6 mt-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-zinc-600 dark:text-zinc-300">Available</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-zinc-400"></div>
                    <span className="text-zinc-600 dark:text-zinc-300">Fully booked</span>
                  </div>
                </div>

                <p className="text-xs mt-3 text-center text-zinc-500 dark:text-zinc-400">
                  You can book up to {maxAdvanceDays} days in advance
                </p>
              </div>
            )}
          </div>

          {/* Time Slot Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              Available Time Slots
            </label>
            <div className="border border-white/20 dark:border-white/10 rounded-2xl p-4 min-h-[100px] bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm">
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Icons.Loading />
                  <span className="ml-2 text-zinc-600 dark:text-zinc-400">
                    Loading available slots...
                  </span>
                </div>
              ) : slots.length === 0 ? (
                <div className="text-center py-4 text-zinc-500 dark:text-zinc-400">
                  <div className="h-8 w-8 mx-auto mb-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                    <svg className="h-4 w-4 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-xs">
                    {!formData.barber_id || !formData.service_id || !selectedDate ? (
                      'Please select a barber, service, and date to see available slots'
                    ) : (
                      'No available slots for the selected date. Try a different date, barber, or service.'
                    )}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm mb-4 text-zinc-600 dark:text-zinc-400">
                    Click on a time slot to select it:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {slots.map((timeSlot, index) => {
                      const slotLabel =
                        timeSlot?.label
                        || timeSlot?.display
                        || timeSlot?.local
                        || timeSlot?.start_local
                        || timeSlot;
                      const slotTime =
                        (typeof timeSlot?.local === 'string' && timeSlot.local)
                        || (typeof timeSlot?.start_local === 'string' && timeSlot.start_local)
                        || (typeof slotLabel === 'string' && slotLabel)
                        || '';
                      const slotUtc =
                        (typeof timeSlot?.start_utc === 'string' && timeSlot.start_utc)
                        || (typeof timeSlot?.utc === 'string' && timeSlot.utc)
                        || (typeof timeSlot?.startUtc === 'string' && timeSlot.startUtc)
                        || (typeof timeSlot?.start === 'string' && timeSlot.start)
                        || '';
                      const displayLabel = typeof slotLabel === 'string' ? slotLabel : String(slotLabel);
                      const isAvailable = timeSlot?.available !== false;
                      
                      // Better selection detection logic
                      let isSelected = false;
                      if (formData.start_utc && (slotUtc || (slotTime && selectedDate))) {
                        if (slotUtc) {
                          isSelected = formData.start_utc === slotUtc;
                        }
                        try {
                          // Create a datetime string for this slot
                          const slotDateTime = slotTime.includes('T')
                            ? new Date(slotTime)
                            : new Date(`${selectedDate}T${slotTime}:00`);
                          const formDateTime = new Date(formData.start_utc);
                          
                          // Compare the dates and times
                          isSelected = slotDateTime.getTime() === formDateTime.getTime();
                          
                          // Debug logging for first few slots and any selected slot
                          if (index < 3 || isSelected) {
                            console.log(`🔍 Slot ${slotTime} selection check:`, {
                              slotTime,
                              slotDateTime: slotDateTime.toISOString(),
                              formDateTime: formDateTime.toISOString(),
                              isSelected,
                              isAvailable,
                              formData_start_utc: formData.start_utc
                            });
                          }
                        } catch (error) {
                          console.warn('Error comparing time slots:', error);
                          // Fallback to string comparison
                          if (slotTime) {
                            isSelected = formData.start_utc.includes(slotTime.replace(':', ''));
                          }
                        }
                      }
                      
                      return (
                        <motion.button
                          key={`${slotTime}-${index}`}
                          whileHover={{ scale: (isSelected || !isAvailable) ? 1 : 1.05 }}
                          whileTap={{ scale: (isSelected || !isAvailable) ? 1 : 0.95 }}
                          type="button"
                          disabled={!isAvailable}
                          onClick={(e) => {
                            if (!isAvailable) return;
                            e.preventDefault();
                            e.stopPropagation();
                            handleSlotSelect(timeSlot, e);
                            return false;
                          }}
                          className={`relative px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${
                            !isAvailable
                              ? 'text-zinc-400 dark:text-zinc-600 border-zinc-300 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800/50 cursor-not-allowed opacity-50'
                              : isSelected
                              ? 'text-white shadow-lg border-transparent'
                              : 'text-zinc-900 dark:text-zinc-100 border-white/20 dark:border-white/10 hover:bg-white/90 dark:hover:bg-zinc-800/90'
                          }`}
                          style={{
                            background: isSelected 
                              ? ACCENT 
                              : 'transparent'
                          }}
                        >
                          {displayLabel}
                          {/* Selection indicator dot similar to calendar */}
                          {isSelected && (
                            <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-white border-2 border-white shadow-sm" />
                          )}
                          {/* Unavailable indicator */}
                          {!isAvailable && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-0.5 h-full bg-red-400 rotate-45 transform origin-center opacity-60" />
                            </div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Selected Time Display */}
          {formData.start_utc && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-4 p-3 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-400 dark:border-emerald-500 text-emerald-700 dark:text-emerald-300 rounded-xl backdrop-blur-sm"
            >
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center mr-3">
                  <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Selected Appointment Time</div>
                  <div className="text-sm">
                    {format(new Date(formData.start_utc), 'EEEE, MMM dd, yyyy')} at {format(new Date(formData.start_utc), 'HH:mm')}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <div className="flex justify-end gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={handleClose} 
              disabled={loading}
              className="px-6 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Cancel
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              disabled={loading || !formData.start_utc}
              onClick={handleSubmit}
              className="text-white px-6 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center shadow-lg hover:shadow-xl transition-all duration-200"
              style={{
                background: loading || !formData.start_utc 
                  ? '#9ca3af' 
                  : `linear-gradient(135deg, ${ACCENT} 0%, #5a4a2a 100%)`
              }}
            >
              {loading ? <Icons.Loading /> : <Icons.Plus />}
              <span className="ml-2">Create Booking</span>
            </motion.button>
          </div>
        </div>
        </motion.div>
      </div>
    </PortalModal>
  );
};

export default CreateBookingModal;
